import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { isEmpty, stringEmpty } from '@app/fremework/functions';
import { MatDivider } from '@angular/material/divider';
import {
  MatList,
  MatListItem,
  MatListItemAvatar,
  MatListItemLine,
  MatListItemTitle,
} from '@angular/material/list';
import { Subject, take } from 'rxjs';
import { Race } from '@app/rest-client/faxe/models/race';
import { RaceFacadeService } from '@app/facade-services/race/race-facade.service';
import { SnackService } from '@app/components/shared/snack/snack.service';
import { takeUntil } from 'rxjs/operators';
import { SelectedRaceService } from '@app/services/selected-race.service';
import { FaxeEventingSimulatorServerService } from '@app/rest-client/faxe-sim/services/faxe-eventing-simulator-server.service';
import { MatButton } from '@angular/material/button';
import { UniqueIdService } from '@app/facade-services/unique-id-service';
import { HttpClient } from '@angular/common/http';
import { SimulationTask } from '@app/rest-client/faxe-sim/models/simulation-task';
import { SignalRService } from '@app/services/signal-r/signal-r.service';
import { MatProgressBar } from '@angular/material/progress-bar';
import { SimulationProgress } from '@app/rest-client/faxe-sim/models/simulation-progress';

interface worker {
  simulationTasks: SimulationTask[];
  completed: number;
  uid: string;
}

@Component({
  selector: 'faxe-sim',
  imports: [
    MatDivider,
    MatList,
    MatListItem,
    MatListItemAvatar,
    MatListItemLine,
    MatListItemTitle,
    MatButton,
    MatProgressBar,
  ],
  templateUrl: './sim.component.html',
  styleUrl: './sim.component.scss',
})
export class SimComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();
  private snackService = inject(SnackService);
  private raceFacadeService = inject(RaceFacadeService);
  private selectedRaceService = inject(SelectedRaceService);
  private simService = inject(FaxeEventingSimulatorServerService);
  private uniqueIdService = inject(UniqueIdService);
  private httpClient = inject(HttpClient);
  private signalRService = inject(SignalRService);
  race: Race;
  workerMap: Map<string, worker> = new Map<string, worker>();

  tasksReceived = 0;
  tasksReceivedSignal = signal(0);
  workersCount = signal(0);
  simulationProgress = signal<SimulationProgress | undefined>(undefined);

  ngOnInit(): void {
    this.raceFacadeService
      .fetchRaceByRaceGuid(
        this.selectedRaceService.selectedRaceGuid,
        'SimComponent'
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((race: Race) => {
        this.race = race;
        this.getSimulationProgress(race);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  startSim(race: Race) {
    this.simService
      .simulateRaceGet({
        RaceGuid: race.raceGuid,
        lowerDelayMs: 10000,
        upperDelayMs: 10000,
      })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.snackService.showSnackBar('Simulator started');
      });
  }

  getSimulationProgress(race: Race) {
    this.simService
      .simulationProgressGet({ raceGuid: race.raceGuid })
      .pipe(take(1))
      .subscribe((progress) => {
        this.simulationProgress.set({ ...progress });
      });
  }

  reserveTask() {
    const uid = this.uniqueIdService.generateUUID();
    this.simService
      .simulationTasksReserveGet({ clientId: uid })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((tasks) => {
        this.tasksReceived += tasks.length;
        this.tasksReceivedSignal.set(this.tasksReceived);
        this.workersCount.set([...this.workerMap.keys()].length);

        if (!isEmpty(tasks)) {
          this.workerMap.set(uid, {
            simulationTasks: tasks,
            completed: 0,
            uid: uid,
          });
          this.handleTasks(uid);
        }
      });
  }

  handleTasks(uid: string) {
    const worker = this.workerMap.get(uid);

    if (!worker) {
      return;
    }

    worker.simulationTasks.forEach((task: SimulationTask) => {
      setTimeout(() => {
        this.handleTask(task);
        worker.completed++;

        if (worker.completed === worker.simulationTasks.length) {
          this.workerMap.delete(uid);
          this.reserveTask();
          this.getSimulationProgress(this.race);
        }
      }, task.delayToNextTaskMs || 0);
    });

    this.workersCount.set([...this.workerMap.keys()].length);
  }

  handleTask(task: SimulationTask) {
    task.taskPayload = task.taskPayload.replace(
      '[[TIMESTAMP]]',
      new Date().toISOString()
    );
    task.taskStarted = new Date().toISOString();
    this.httpClient
      .post(`${task.taskEndpoint}${this.parseQuery(task)}`, task.taskPayload, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(take(1))
      .subscribe((response) => {
        task.taskCompleted = new Date().toISOString();
        this.simService
          .simulationTasksResultPost({ body: task })
          .pipe(take(1))
          .subscribe();
      });
  }

  parseQuery(task: SimulationTask) {
    let query = '';

    if (!isEmpty(task.taskParameters)) {
      query = '?';
      task.taskParameters.forEach((parameter) => {
        parameter.value = parameter.value.replace(
          '[[TIMESTAMP]]',
          new Date().toISOString()
        );
        query += `${parameter.key}=${parameter.value}&`;
      });
    }

    if (!stringEmpty(query)) {
      query = query.trim().replace(/&$/, '');
    }
    return query;
  }

  stopSignalR() {
    this.signalRService.stop('SimComponent');
  }
}
